import currency from 'currency.js'
import { enUS, th } from 'date-fns/locale'
import i18next from 'i18next'
import axios from 'axios'
import 'vant/es/notify/style'

export function getParamsFromLiffUrl(url: string): URLSearchParams {
  const pathString = decodeURIComponent(decodeURIComponent(url)).replace('?liff.state=', '')
  const indexOfQueryString = pathString.lastIndexOf('?')
  const queryString = pathString.substring(indexOfQueryString)
  const params = new URLSearchParams(queryString)
  return params
}

export const validateRequiredParams = (requiredParams: string[], params: URLSearchParams): void => {
  requiredParams.forEach(param => {
    if (!params.has(param)) {
      throw new Error(`Missing ${param}`)
    }
  })
}

// base URL ของ LINE CRM API (apps/api) - ใช้ต่อ path ตรงๆ เช่น `${crmUrl}/api/logout`
// ต้องมี scheme เสมอ ถ้าไม่มี axios จะมองเป็น relative path แล้วไปต่อท้าย path หน้าปัจจุบัน
// (เช่น /profile/abc-uat-api-line-crm.lamunpunit.com/api/logout)
export function setUpCrmUrl(): string {
  const crm_url = import.meta.env.VITE_LINE_CRM_URL
  if (!crm_url) {
    if (import.meta.env.PROD) {
      // build ที่ไม่ได้ส่ง VITE_LINE_CRM_URL มา จะใช้ค่า default เป็น UAT
      return 'https://abc-uat-api-line-crm.lamunpunit.com'
    }
    // dev (local) ต้องตั้ง VITE_LINE_CRM_URL ใน .env เสมอ
    throw new Error('invalid crm environment')
  }

  // กันกรณีตั้ง env เป็นแค่ host ไม่มี https://
  return /^https?:\/\//.test(crm_url) ? crm_url : `https://${crm_url}`

  // if (subdomain.includes('staging')) {
  //   return 'https://stg-api-line-crm.lamunpunit.com'
  // } else if (subdomain.includes('uat')) {
  //   return 'https://uat-api-line-crm.lamunpunit.com'
  // } else if (!import.meta.env.PROD) {
  //   return import.meta.env.VITE_LINE_CRM_URL
  // } else {
  //   return 'https://line-crm.jeracloud.com'
  // }
}

export function getAPIUrlFromClinicType(clinicType: string): string {
  if (!clinicType) {
    throw new Error('Clinic type not specified')
  }
  const JERA_CONFIG_SUBDOMAIN: Record<string, string> = {
    C1: 'https://api.jeracloud.com',
    C2: 'https://api2.jeracloud.com',
    A1: 'https://abc.jeracloud.com',
    A2: 'https://abc-uat.jeracloud.com',
    A3: 'https://abc-dev.jeracloud.com',
    D1: 'https://api.jeradent.com',
    U1: 'https://uat.jeracloud.com',
    S1: 'https://staging.jeracloud.com',
    X1: import.meta.env.VITE_JERA_URL,
  }
  return JERA_CONFIG_SUBDOMAIN[clinicType]
}

// Get subdomain from window.location.host without port
// Example https://uat.jeracloud.com:3000 => uat
// Example https://jeracloud.com => jeracloud
// Example http://localhost:3000 => localhost
// Example http://localhost => localhost
// Example https://uat.jeracloud.com => uat
export function getSubdomainFromHost() {
  const host = window.location.host
  const hostWithoutPort = host.split(':')[0]
  const subdomain = hostWithoutPort.split('.')[0]
  return subdomain
}

function isSupportLS() {
  try {
    localStorage.setItem('_test-key', 'hi')
    localStorage.getItem('_test-key')
    localStorage.removeItem('_test-key')
    return true
  } catch (e) {
    return false
  }
}

class Memory {
  cache: any
  constructor() {
    this.cache = {}
  }
  setItem(cacheKey: string, data: any) {
    this.cache[cacheKey] = data
  }
  getItem(cacheKey: string) {
    return this.cache[cacheKey]
  }
  removeItem(cacheKey: string) {
    this.cache[cacheKey] = undefined
  }
}
// if not support localStorage, fallback to memory
export const storage = isSupportLS() ? window.localStorage : new Memory()

export function stringToUUID(input: string): string {
  const uuidRx = new RegExp(/([a-zA-Z0-9]{8})([a-zA-Z0-9]{4})([a-zA-Z0-9]{4})([a-zA-Z0-9]{5})([a-zA-Z0-9]{11})/)
  const uuid = input.replace(uuidRx, '$1-$2-$3-$4-$5')
  return uuid
}

export const useCurrencyFormat = (number: string | number | currency) => {
  const price = new currency(number)

  if (price.cents() > 0) {
    // ถ้ามีทศนิยม จะแสดงที่ 2 ตำแหน่ง
    return price.format({ symbol: '', separator: ',' })
  }

  // ถ้าไม่มี จะไม่แสดงตำแหน่งเลย
  return currency(price, { precision: 0 }).format({
    symbol: '',
    separator: ',',
  })
}

export const getDateFnsLocaleLang = () => {
  if (i18next.language === 'th') {
    return th
  }
  return enUS
}

// pagination generics
export async function fetchPaginated<T>(url: string, params?: Record<string, any>): Promise<T> {
  const { data } = await axios.get<T>(url, { params })
  return data
}

export function normalizePaginatedNextUrl(nextUrl: string, baseUrl?: string): string {
  if (!nextUrl || !baseUrl) return nextUrl

  const normalizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  const { pathname, search, hash } = new URL(nextUrl, normalizedBase)
  return `${normalizedBase}${pathname}${search}${hash}`
}

export async function fetchPaginatedNextPage<T>(nextUrl: string, baseUrl?: string): Promise<T> {
  const requestUrl = normalizePaginatedNextUrl(nextUrl, baseUrl)
  const { data } = await axios.get<T>(requestUrl)
  return data
}
